import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ThreeDCardDemo from "../ThreeDCardDemo";
import { SparklesCore } from "../ui/sparkles";
import {
  IconApi,
  IconArrowUpRight,
  IconBolt,
  IconBrandGoogle,
  IconBrandInstagram,
  IconChartBar,
  IconChartDots3,
  IconDatabase,
  IconDeviceMobile,
  IconLayersLinked,
  IconSearch,
  IconSparkles,
  IconTarget,
  IconTimeline,
  IconUsers,
} from "@tabler/icons-react";
import { fadeUp, fadeLeft, fadeRight, stagger, cardAnimation } from "./animations";

export function WebApplicationSection() {
  return (
    <ServiceSection
      number="01"
      label="Custom Software"
      title={
        <>
          Web Applications
          <span className="block text-neutral-400">
            built around your workflow.
          </span>
        </>
      }
      description="Turn complex business processes into simple, reliable digital systems. We create custom web applications that help businesses manage operations, customers, data, teams and everyday workflows."
      cta="Build a Web Application"
    >
      <WebAppVisual />
    </ServiceSection>
  );
}

export function LandingPageSection() {
  return (
    <ServiceSection
      number="02"
      label="Conversion-Focused Design"
      title={
        <>
          One page.
          <span className="block">One clear action.</span>
        </>
      }
      description="A landing page should do more than look attractive. We design focused experiences that communicate your offer clearly, establish trust quickly and move visitors toward meaningful action."
      cta="Create My Landing Page"
      gray
    >
    <LandingPageVisual />
    </ServiceSection>
  );
}

export function DigitalMarketingServiceSection() {
  return (
    <ServiceSection
      number="03"
      label="Digital Growth"
      title={
        <>
          Be visible where
          <span className="block text-neutral-400">
            your customers are looking.
          </span>
        </>
      }
      description="Good marketing is not about being everywhere. It is about reaching the right audience with the right message at the right moment."
      cta="Grow My Business"
    >
      <MarketingVisual />
    </ServiceSection>
  );
}

export function AppDevelopmentSection() {
  return (
    <ServiceSection
      number="04"
      label="Mobile Experiences"
      title={
        <>
          Your business,
          <span className="block text-neutral-400">in their hands.</span>
        </>
      }
      description="We design and develop mobile experiences that feel simple, fast and natural. From customer-facing applications to internal business tools, every app is built around usability and long-term growth."
      cta="Build My App"
      gray
    >
      <AppDevelopmentVisual />
    </ServiceSection>
  );
}

export function AnimatedWebsiteSection() {
  return (
        <section
          className="
            relative
            overflow-hidden
            bg-[#0c0c0c]
            py-20
            text-white
            sm:py-24
            lg:py-32
          "
        >
          {/* GLOW */}

          <div
            className="
              pointer-events-none
              absolute
              left-1/2
              top-0
              h-[600px]
              w-[600px]
              -translate-x-1/2
              rounded-full
              bg-[#55aef3]/10
              blur-[140px]
            "
          />

          <div
            className="
              relative
              z-10
              mx-auto
              max-w-7xl
              px-5
              sm:px-8
              lg:px-10
            "
          >
            {/* HEADING */}

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
              }}
            >
              <div
                className="
                  flex
                  items-center
                  gap-4
                "
              >
                <span
                  className="
                    merriweather
                    text-5xl
                    font-light
                    text-white/15
                  "
                >
                  05
                </span>

                <SectionLabel dark>Immersive Web Experiences</SectionLabel>
              </div>

              <div
                className="
                  mt-7
                  grid
                  gap-8
                  lg:grid-cols-[1fr_0.75fr]
                  lg:items-end
                "
              >
                <h2
                  className="
                    merriweather
                    text-3xl
                    font-black
                    tracking-[-0.04em]
                    sm:text-4xl
                    lg:text-[56px]
                    lg:leading-[1.08]
                  "
                >
                  Some websites are visited.
                  <span className="block text-[#55aef3]">
                    Others are experienced.
                  </span>
                </h2>

                <p
                  className="
                    max-w-xl
                    text-[15px]
                    leading-8
                    text-white/55
                  "
                >
                  For brands that want more than a conventional website, we create
                  immersive experiences where movement, storytelling and
                  interaction work together.
                </p>
              </div>
            </motion.div>

            {/* =================================================
                YOUR ANIMATION AREA
            ================================================= */}

            <motion.div
              initial={{
                opacity: 0,
                y: 50,
                scale: 0.97,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
                scale: 1,
              }}
              viewport={{
                once: true,
                amount: 0.15,
              }}
              transition={{
                duration: 0.9,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="
                relative
                mt-14
                overflow-hidden
                rounded-[24px]
                border
                border-white/10
                bg-[#141414]
              "
            >
              {/* TOPBAR */}

              <div
                className="
                  flex
                  items-center
                  justify-between
                  border-b
                  border-white/10
                  px-5
                  py-4
                "
              >
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
                  <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
                  <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
                </div>

                <span
                  className="
                    text-[9px]
                    font-bold
                    uppercase
                    tracking-[0.2em]
                    text-white/30
                  "
                >
                  Experience The Motion
                </span>
              </div>

              {/* ===============================================
                  ADD YOUR ANIMATION INSIDE THIS DIV
              =============================================== */}
              <div
                id="animated-website-experience"
                className="
      relative
      flex
      min-h-[520px]
      items-center
      justify-center
      overflow-hidden
      sm:min-h-[620px]
      lg:min-h-[700px]
    "
              >
                {/* =====================================================
        BACKGROUND GRID
    ===================================================== */}

                <div
                  className="
        pointer-events-none
        absolute
        inset-0
        opacity-[0.08]
      "
                  style={{
                    backgroundImage:
                      "linear-gradient(rgba(255,255,255,.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.4) 1px, transparent 1px)",
                    backgroundSize: "45px 45px",
                  }}
                />

                {/* =====================================================
        ANIMATION 01 — SPARKLES
    ===================================================== */}

                <div
                  className="
        pointer-events-none
        absolute
        inset-0
        z-[1]
      "
                >
                  <SparklesCore
                    id="we3x-technology-sparkles"
                    background="transparent"
                    minSize={0.4}
                    maxSize={1.4}
                    particleDensity={100}
                    speed={0.7}
                    particleColor="#55aef3"
                    className="
          h-full
          w-full
        "
                  />
                </div>

                {/* BLUE CENTER GLOW */}

                <div
                  className="
        pointer-events-none
        absolute
        left-1/2
        top-1/2
        z-[2]
        h-[500px]
        w-[500px]
        -translate-x-1/2
        -translate-y-1/2
        rounded-full
        bg-[#55aef3]/10
        blur-[130px]
      "
                />

                {/* =====================================================
        ANIMATION 02 — FLOATING + 3D CARD
    ===================================================== */}

                <motion.div
                  initial={{
                    opacity: 0,
                    y: 60,
                    scale: 0.94,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                    scale: 1,
                  }}
                  viewport={{
                    once: true,
                    amount: 0.2,
                  }}
                  transition={{
                    duration: 0.9,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="
        relative
        z-10
        w-full
        px-4
        sm:px-8
      "
                >
                  {/* CONTINUOUS FLOAT */}

                  <motion.div
                    animate={{
                      y: [0, -10, 0],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <ThreeDCardDemo />
                  </motion.div>
                </motion.div>

                {/* =====================================================
        BOTTOM LIGHT
    ===================================================== */}

                <div
                  className="
        pointer-events-none
        absolute
        bottom-0
        left-1/2
        z-[3]
        h-[1px]
        w-[70%]
        -translate-x-1/2
        bg-gradient-to-r
        from-transparent
        via-[#55aef3]
        to-transparent
      "
                />

                <div
                  className="
        pointer-events-none
        absolute
        bottom-0
        left-1/2
        h-24
        w-[60%]
        -translate-x-1/2
        bg-[#55aef3]/10
        blur-[70px]
      "
                />
              </div>
            </motion.div>

            {/* CAPABILITIES */}

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
              }}
              className="
                mt-7
                grid
                gap-px
                overflow-hidden
                rounded-[18px]
                bg-white/10
                sm:grid-cols-2
                lg:grid-cols-4
              "
            >
              <DarkCapability icon={<IconTimeline />} title="Scroll Animation" />

              <DarkCapability
                icon={<IconSparkles />}
                title="Cinematic Transitions"
              />

              <DarkCapability
                icon={<IconLayersLinked />}
                title="Interactive Sections"
              />

              <DarkCapability icon={<IconBolt />} title="Micro Interactions" />
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
              }}
              className="
                mt-12
                flex
                flex-col
                justify-between
                gap-7
                border-t
                border-white/10
                pt-8
                md:flex-row
                md:items-center
              "
            >
              <p
                className="
                  merriweather
                  max-w-2xl
                  text-xl
                  font-bold
                  leading-relaxed
                  sm:text-2xl
                "
              >
                Don't just show your brand.
                <span className="text-[#55aef3]"> Make people feel it.</span>
              </p>

              <Link
                to="/contact"
                className="
                  group
                  inline-flex
                  w-fit
                  items-center
                  gap-2
                  rounded-[9px]
                  bg-white
                  px-6
                  py-3.5
                  text-sm
                  font-bold
                  text-black
                  transition-all
                  hover:-translate-y-1
                  hover:bg-[#55aef3]
                  hover:text-white
                "
              >
                Create an Animated Website
                <IconArrowUpRight
                  size={17}
                  className="
                    transition-transform
                    group-hover:translate-x-1
                    group-hover:-translate-y-1
                  "
                />
              </Link>
            </motion.div>
          </div>
        </section>
  );
}

export function VideoEditingSection() {
  return (
    <ServiceSection
      number="06"
      label="Visual Storytelling"
      title={
        <>
          Turn raw footage
          <span className="block text-neutral-400">
            into something worth watching.
          </span>
        </>
      }
      description="Every video has a story hidden inside it. We shape footage, sound, pacing, motion and visual elements into polished content designed to capture attention and communicate your message clearly."
      cta="Start a Video Project"
      gray
    >
      <VideoEditingVisual />
    </ServiceSection>
  );
}

function ServiceSection({
  number,
  label,
  title,
  description,
  cta,
  children,
  gray = false,
}) {
  return (
    <section
      className={`
        py-20
        sm:py-24
        lg:py-28
        ${gray ? "bg-[#f6f6f6]" : "bg-white"}
      `}
    >
      <div
        className="
          mx-auto
          max-w-7xl
          px-5
          sm:px-8
          lg:px-10
        "
      >
        <div
          className="
            grid
            gap-12
            lg:grid-cols-[0.72fr_1.28fr]
            lg:gap-16
          "
        >
          {/* CONTENT */}

          <motion.div
            variants={fadeLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.2,
            }}
          >
            <div className="flex items-start gap-5">
              <span
                className="
                  merriweather
                  text-5xl
                  font-light
                  text-neutral-200
                "
              >
                {number}
              </span>

              <div>
                <SectionLabel>{label}</SectionLabel>

                <h2
                  className="
                    merriweather
                    mt-4
                    text-3xl
                    font-black
                    tracking-[-0.04em]
                    sm:text-4xl
                    lg:text-[46px]
                    lg:leading-[1.08]
                  "
                >
                  {title}
                </h2>
              </div>
            </div>

            <p
              className="
                mt-6
                max-w-lg
                text-[15px]
                leading-8
                text-neutral-600
              "
            >
              {description}
            </p>

            <Link
              to="/contact"
              className="
                group
                mt-7
                inline-flex
                items-center
                gap-2
                text-sm
                font-bold
              "
            >
              {cta}

              <IconArrowUpRight
                size={16}
                className="
                  transition-transform
                  group-hover:translate-x-1
                  group-hover:-translate-y-1
                "
              />
            </Link>
          </motion.div>

          {/* VISUAL */}

          <motion.div
            variants={fadeRight}
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.15,
            }}
          >
            {children}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function WebAppVisual() {
  return (
    <div
      className="
        overflow-hidden
        rounded-[22px]
        border
        border-neutral-200
        bg-[#101010]
      "
    >
      <div
        className="
          flex
          items-center
          justify-between
          border-b
          border-white/10
          px-5
          py-4
        "
      >
        <div className="flex gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        </div>

        <span className="text-[10px] text-white/30">WE3X Business System</span>
      </div>

      <div
        className="
          grid
          gap-3
          p-5
          sm:grid-cols-[0.33fr_1fr]
        "
      >
        {/* SIDEBAR */}

        <div
          className="
            hidden
            rounded-[14px]
            bg-white/[0.05]
            p-4
            sm:block
          "
        >
          <div className="h-8 w-8 rounded-lg bg-[#55aef3]" />

          <div className="mt-7 space-y-3">
            {[1, 2, 3, 4, 5].map((item) => (
              <div
                key={item}
                className="
                  h-8
                  rounded-lg
                  bg-white/[0.05]
                "
              />
            ))}
          </div>
        </div>

        {/* DASHBOARD */}

        <div className="rounded-[14px] bg-[#f6f6f6] p-5">
          <div
            className="
              grid
              gap-3
              sm:grid-cols-3
            "
          >
            <DashboardMetric title="Customers" value="1,248" />

            <DashboardMetric title="Orders" value="856" />

            <DashboardMetric title="Growth" value="+28%" />
          </div>

          <div
            className="
              mt-4
              grid
              gap-3
              md:grid-cols-[1.4fr_0.6fr]
            "
          >
            <div
              className="
                rounded-xl
                bg-white
                p-4
              "
            >
              <p className="text-xs font-bold">Performance</p>

              <div
                className="
                  mt-7
                  flex
                  h-32
                  items-end
                  gap-2
                "
              >
                {[35, 52, 44, 70, 58, 82, 75, 94].map((height, index) => (
                  <motion.div
                    key={index}
                    initial={{
                      height: 0,
                    }}
                    whileInView={{
                      height: `${height}%`,
                    }}
                    viewport={{
                      once: true,
                    }}
                    transition={{
                      duration: 0.8,
                      delay: index * 0.07,
                    }}
                    className="
                        flex-1
                        rounded-t
                        bg-[#55aef3]
                      "
                  />
                ))}
              </div>
            </div>

            <div className="grid gap-3">
              <DashboardFeature
                icon={<IconDatabase size={18} />}
                title="Database"
              />

              <DashboardFeature icon={<IconApi size={18} />} title="API" />

              <DashboardFeature icon={<IconUsers size={18} />} title="Users" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LandingPageVisual() {
  return (
    <div
      className="
        relative
        overflow-hidden
        rounded-[22px]
        border
        border-neutral-200
        bg-white
        p-5
        sm:p-7
      "
    >
      <div
        className="
          flex
          items-center
          gap-2
          border-b
          border-neutral-200
          pb-4
        "
      >
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-200" />
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-200" />
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-200" />

        <div
          className="
            ml-3
            h-7
            flex-1
            rounded-full
            bg-[#f6f6f6]
          "
        />
      </div>

      <div
        className="
          mt-6
          grid
          items-center
          gap-6
          sm:grid-cols-[1fr_0.8fr]
        "
      >
        <div>
          <div
            className="
              h-2
              w-20
              rounded-full
              bg-[#55aef3]
            "
          />

          <div className="mt-5 h-7 w-[90%] rounded bg-neutral-950" />

          <div className="mt-3 h-7 w-[65%] rounded bg-neutral-950" />

          <div className="mt-6 space-y-2">
            <div className="h-2 w-full rounded bg-neutral-100" />
            <div className="h-2 w-[85%] rounded bg-neutral-100" />
            <div className="h-2 w-[70%] rounded bg-neutral-100" />
          </div>

          <motion.div
            whileHover={{
              scale: 1.04,
            }}
            className="
              mt-7
              inline-flex
              rounded-lg
              bg-[#151515]
              px-5
              py-3
              text-[10px]
              font-bold
              text-white
            "
          >
            Take Action
          </motion.div>
        </div>

        <motion.div
          animate={{
            y: [0, -8, 0],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="
            relative
            min-h-[250px]
            rounded-[18px]
            bg-blue-50
          "
        >
          <IconTarget
            size={62}
            stroke={1.2}
            className="
              absolute
              left-1/2
              top-1/2
              -translate-x-1/2
              -translate-y-1/2
              text-[#55aef3]
            "
          />
        </motion.div>
      </div>
    </div>
  );
}

function MarketingVisual() {
  return (
    <div
      className="
        grid
        gap-4
        md:grid-cols-[1.25fr_0.75fr]
      "
    >
      <div
        className="
          rounded-[20px]
          border
          border-neutral-200
          bg-white
          p-6
        "
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-neutral-400">Campaign performance</p>

            <h3 className="mt-1 text-xl font-bold">Growth overview</h3>
          </div>

          <IconChartDots3 size={28} className="text-[#55aef3]" />
        </div>

        <div className="mt-8 space-y-5">
          <MarketingBar label="SEO" width="92%" />

          <MarketingBar label="Google Ads" width="84%" />

          <MarketingBar label="Social Media" width="88%" />

          <MarketingBar label="Content" width="75%" />
        </div>
      </div>

      <div className="grid gap-3">
        <MarketingTool icon={<IconSearch />} title="SEO" />

        <MarketingTool icon={<IconBrandGoogle />} title="Google Ads" />

        <MarketingTool icon={<IconBrandInstagram />} title="Social Media" />

        <MarketingTool icon={<IconChartBar />} title="Analytics" />
      </div>
    </div>
  );
}

function AppDevelopmentVisual() {
  return (
    <div
      className="
        relative
        flex
        min-h-[500px]
        items-center
        justify-center
        overflow-hidden
        rounded-[22px]
        bg-[#101010]
        p-6
      "
    >
      <div
        className="
          absolute
          h-[350px]
          w-[350px]
          rounded-full
          bg-[#55aef3]/10
          blur-[100px]
        "
      />

      {/* PHONE 1 */}

      <motion.div
        initial={{
          rotate: -10,
          x: 40,
        }}
        whileInView={{
          rotate: -6,
          x: 0,
        }}
        viewport={{
          once: true,
        }}
        transition={{
          duration: 0.9,
        }}
        className="
          relative
          z-10
          -mr-10
          h-[360px]
          w-[185px]
          overflow-hidden
          rounded-[30px]
          border-[6px]
          border-[#272727]
          bg-white
          shadow-2xl
        "
      >
        <PhoneScreen type="light" />
      </motion.div>

      {/* PHONE 2 */}

      <motion.div
        initial={{
          y: 40,
        }}
        whileInView={{
          y: 0,
        }}
        viewport={{
          once: true,
        }}
        transition={{
          duration: 0.9,
        }}
        className="
          relative
          z-20
          h-[410px]
          w-[205px]
          overflow-hidden
          rounded-[34px]
          border-[6px]
          border-[#272727]
          bg-white
          shadow-2xl
        "
      >
        <PhoneScreen />
      </motion.div>

      {/* PHONE 3 */}

      <motion.div
        initial={{
          rotate: 10,
          x: -40,
        }}
        whileInView={{
          rotate: 6,
          x: 0,
        }}
        viewport={{
          once: true,
        }}
        transition={{
          duration: 0.9,
        }}
        className="
          relative
          z-10
          -ml-10
          h-[350px]
          w-[180px]
          overflow-hidden
          rounded-[30px]
          border-[6px]
          border-[#272727]
          bg-white
          shadow-2xl
        "
      >
        <PhoneScreen type="light" />
      </motion.div>
    </div>
  );
}

function VideoEditingVisual() {
  return (
    <div
      className="
        overflow-hidden
        rounded-[22px]
        border
        border-neutral-200
        bg-[#101010]
        text-white
      "
    >
      {/* VIDEO */}
      <div
        className="
          relative
          aspect-video
          overflow-hidden
          bg-black
        "
      >
        <video
          controls
          playsInline
          className="
            h-full
            w-full
            object-cover
          "
        >
          <source src="/videos/video-editing-showreel.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>

      {/* TIMELINE */}
      <div className="p-5">
        <div className="flex items-center justify-between text-[10px] text-white/30">
          <span>00:00</span>
          <span>00:30</span>
        </div>

        <div className="mt-3 space-y-2">
          <TimelineTrack width="90%" blue />
          <TimelineTrack width="72%" />
          <TimelineTrack width="82%" blue />
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children, dark = false }) {
  return (
    <div
      className={`
        flex
        items-center
        gap-3
        text-[10px]
        font-bold
        uppercase
        tracking-[0.2em]
        ${dark ? "text-[#55aef3]" : "text-[#3899e4]"}
      `}
    >
      <span className="h-[1px] w-8 bg-[#55aef3]" />

      {children}
    </div>
  );
}

function DashboardMetric({ title, value }) {
  return (
    <div
      className="
        rounded-xl
        bg-white
        p-4
      "
    >
      <p className="text-[10px] text-neutral-400">{title}</p>

      <p className="mt-2 text-xl font-bold">{value}</p>
    </div>
  );
}

function DashboardFeature({ icon, title }) {
  return (
    <div
      className="
        rounded-xl
        bg-white
        p-4
      "
    >
      <div className="text-[#55aef3]">{icon}</div>

      <p className="mt-3 text-[11px] font-bold">{title}</p>
    </div>
  );
}

function MarketingBar({ label, width }) {
  return (
    <div>
      <div
        className="
          flex
          items-center
          justify-between
          text-xs
          font-semibold
        "
      >
        <span>{label}</span>

        <span className="text-neutral-400">{width}</span>
      </div>

      <div
        className="
          mt-2
          h-1.5
          overflow-hidden
          rounded-full
          bg-neutral-100
        "
      >
        <motion.div
          initial={{
            width: 0,
          }}
          whileInView={{
            width,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 1,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="
            h-full
            rounded-full
            bg-[#55aef3]
          "
        />
      </div>
    </div>
  );
}

function MarketingTool({ icon, title }) {
  return (
    <motion.div
      whileHover={{
        x: 4,
      }}
      className="
        flex
        items-center
        gap-4
        rounded-[15px]
        border
        border-neutral-200
        bg-white
        p-4
      "
    >
      <div
        className="
          flex
          h-9
          w-9
          items-center
          justify-center
          rounded-lg
          bg-blue-50
          text-[#55aef3]
        "
      >
        {icon}
      </div>

      <span className="text-xs font-bold">{title}</span>
    </motion.div>
  );
}

function PhoneScreen() {
  return (
    <div className="h-full bg-[#f6f6f6] p-4">
      <div className="mx-auto h-1.5 w-12 rounded-full bg-neutral-200" />

      <div
        className="
          mt-6
          rounded-[16px]
          bg-[#55aef3]
          p-4
          text-white
        "
      >
        <IconDeviceMobile size={22} />

        <p className="mt-5 text-[10px] text-white/70">Welcome</p>

        <p className="mt-1 text-sm font-bold">Your App</p>
      </div>

      <div
        className="
          mt-4
          grid
          grid-cols-2
          gap-2
        "
      >
        {[1, 2, 3, 4].map((item) => (
          <div
            key={item}
            className="
              h-16
              rounded-xl
              bg-white
            "
          />
        ))}
      </div>

      <div
        className="
          mt-4
          h-20
          rounded-xl
          bg-white
        "
      />
    </div>
  );
}

function DarkCapability({ icon, title }) {
  return (
    <motion.div
      variants={cardAnimation}
      className="
        bg-[#0c0c0c]
        p-6
      "
    >
      <div className="text-[#55aef3]">{icon}</div>

      <p
        className="
          mt-5
          text-xs
          font-bold
          text-white/80
        "
      >
        {title}
      </p>
    </motion.div>
  );
}

function TimelineTrack({ width, blue = false }) {
  return (
    <div
      className="
        relative
        h-8
        rounded-md
        bg-white/[0.05]
      "
    >
      <motion.div
        initial={{
          width: 0,
        }}
        whileInView={{
          width,
        }}
        viewport={{
          once: true,
        }}
        transition={{
          duration: 1,
        }}
        className={`
          h-full
          rounded-md
          ${blue ? "bg-[#55aef3]/70" : "bg-white/15"}
        `}
      />
    </div>
  );
}
